// @figma file=tbd node=tbd name="ResourceCard" set="tbd" verified=tbd

import * as React from 'react';
import { cn } from '../../lib/utils';
import { Spinner } from '../atoms/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../forms/primitives/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../molecules/pagination';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ResourceCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Button or node rendered top-right (e.g. "New Item" button) */
  action?: React.ReactNode;
  /** Current page size */
  pageSize: number;
  /** Called when the user changes rows-per-page */
  onPageSizeChange: (size: number) => void;
  /** Available page size options (default: [5, 10, 25]) */
  pageSizeOptions?: number[];
  /** Filter controls rendered below the toolbar (search, selects, etc.) */
  filters?: React.ReactNode;
  /** Show a loading spinner instead of children */
  isLoading?: boolean;
  /** Show the empty message instead of children */
  isEmpty?: boolean;
  /** Message shown when isEmpty is true */
  emptyMessage?: string;
  /** First visible row number (1-based) */
  from: number;
  /** Last visible row number (inclusive) */
  to: number;
  /** Total filtered row count */
  total: number;
  /** Current zero-based page index */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Called when the user navigates to a different page */
  onPageChange: (page: number) => void;
  /** Returns the page number sequence (numbers are 0-based, 'ellipsis' is a gap marker) */
  pageNumbers: () => (number | 'ellipsis')[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const ResourceCard = React.forwardRef<HTMLDivElement, ResourceCardProps>(
  (
    {
      className,
      children,
      action,
      pageSize,
      onPageSizeChange,
      pageSizeOptions = [5, 10, 25],
      filters,
      isLoading = false,
      isEmpty = false,
      emptyMessage = 'No items found.',
      from,
      to,
      total,
      page,
      totalPages,
      onPageChange,
      pageNumbers,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm',
          className,
        )}
        {...props}
      >
        {/* ── Toolbar ───────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6">
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>

        {/* ── Filters ───────────────────────────────────────────────────── */}
        {filters && (
          <div className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:px-6">
            {filters}
          </div>
        )}

        {/* ── Table area — only this scrolls ────────────────────────────── */}
        <div className="min-w-0 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : isEmpty ? (
            <div className="py-12 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          ) : (
            children
          )}
        </div>

        {/* ── Pagination footer ─────────────────────────────────────────── */}
        <div className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 sm:flex-row sm:px-6">
          <p className="text-sm text-muted-foreground">
            {total === 0 ? 'No entries' : `Showing ${from} to ${to} of ${total} entries`}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(Math.max(0, page - 1))}
                  aria-disabled={page === 0}
                  className={page === 0 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {pageNumbers().map((p, i) =>
                p === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      onClick={() => onPageChange(p)}
                      className="cursor-pointer"
                    >
                      {p + 1}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
                  aria-disabled={page >= totalPages - 1}
                  className={
                    page >= totalPages - 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    );
  },
);

ResourceCard.displayName = 'ResourceCard';

export { ResourceCard };
